<template>
  <wa-page
    view-slug="icon-discovery"
    :is-showing-v7-prerelease-notice="true"
  >
    <wa-page-header slot="header" />
    <main
      slot="content"
      class="app-content"
      style="--desktop-grid-max-width: var(--viewport-desktop)"
      role="main"
    >
      <wa-icons-loading v-if="isLoading" />
      <ais-instant-search
        v-else
        ref="instantSearch"
        :search-client="searchClient"
        :index-name="index"
        :routing="routing"
        :middlewares="middlewares"
        :search-parameters="{ clickAnalytics: true }"
        :insights-client="insightsClient"
        :future="{ preserveSharedStateOnUnmount: true }"
      >
        <ais-configure
          :hits-per-page.camel="pageSize"
          :distinct="false"
          :facet-filters.camel="facetFilters"
          :filters="filters"
        />

        <section
          id="search-header"
          class="padding-top-2xl"
          style="background: var(--white)"
        >
          <div class="container">
            <div class="margin-bottom-2xl margin-auto laptop:line-length-xl">
              <h1 class="sr-only">Font Awesome Icons</h1>

              <ais-search-box id="aisSearchBox">
                <template slot-scope="{ currentRefinement }">
                  <input-term
                    :term="searchTerm"
                    :label="`Search the v${$releases.active.versionParts.major} Icons`"
                    :is-showing-clear-button="!!currentRefinement"
                    style="--input-placeholder-color: var(--fa-md-gravy)"
                    @input="(term) => setSearchTerm(term)"
                    @clear="clearSearch('search-bar')"
                    @focus="searchFieldFocus = true"
                    @blur="searchFieldFocus = false"
                  />
                </template>
              </ais-search-box>
            </div>

            <div class="display-flex flex-column-reverse laptop:flex-row laptop:flex-items-center flex-content-between">
              <div
                class="display-flex laptop:margin-right-xl flex-content-center size-sm"
                style="gap: var(--spacing-xs)"
              >
                <search-tabs
                  :version="version"
                  :selected="selectedTab"
                  @applyFilter="applyFilter"
                />
              </div>

              <div
                class="display-flex flex-row flex-items-center flex-content-center tablet:flex-content-between flex-wrap margin-bottom-2xl laptop:margin-bottom-0 laptop:margin-left-lg laptop:line-length-xl laptop:line-length-none"
                style="
                  --button-margin-bottom: 0;
                  --button-background: transparent;
                  --button-color: var(--fa-navy);
                  --button-font-weight: var(--font-weight-normal);
                  --button-hover-color: var(--fa-dk-blue);
                  --border-color: var(--fa-lt-gravy);
                  --button-transition-duration: var(--timing-2xfast);
                  gap: var(--spacing-xl);
                "
              >
                <button
                  class="flat icons-facets-menu-toggle with-border laptop:display-none"
                  style="flex-grow: 1; flex-shrink: 0; flex-basis: auto; --button-padding: var(--spacing-sm) var(--button-padding-horizontal)"
                  :aria-label="facetsMenuLabel"
                  data-balloon-pos="up"
                  @click="toggleFacetsMenu"
                >
                  <font-awesome-icon
                    v-if="isShowingFacetsMenu"
                    :icon="$v6.solid.faBarsFilter"
                    fixed-width
                    size="lg"
                    flip="vertical"
                  />
                  <font-awesome-icon
                    v-else
                    :icon="$v6.solid.faBarsFilter"
                    fixed-width
                    size="lg"
                  />
                  {{ facetsMenuLabel }}
                </button>

                <div
                  class="display-flex flex-items-center"
                  style="
                    --input-background: transparent;
                    --input-border-color: var(--fa-lt-gravy);
                    --input-box-shadow: none;
                    --input-padding-horizontal: var(--spacing-xl);
                    --border-color: var(--fa-lt-gravy);
                    --button-hover-border-color: var(--fa-gravy);
                    --button-active-border-width: 2px;
                  "
                >
                  <div
                    class="buttons display-flex margin-x-sm tablet:margin-x-lg"
                    style="--button-padding: var(--spacing-sm) var(--spacing-md)"
                  >
                    <button
                      class="flat with-border"
                      :style="{ '--button-color': resultsView === 'roomy' ? 'var(--link-color)' : 'var(--fa-navy)' }"
                      aria-label="Roomy"
                      data-balloon-pos="up"
                      @click.prevent="changeResultsView('roomy')"
                    >
                      <font-awesome-icon
                        :icon="$v6.solid.faGrid2"
                        size="lg"
                      />
                      <span class="sr-only">Show icons in roomy display</span>
                    </button>
                    <button
                      class="flat with-border"
                      :style="{ '--button-color': resultsView === 'compact' ? 'var(--link-color)' : 'var(--fa-navy)' }"
                      aria-label="Compact"
                      data-balloon-pos="up"
                      @click.prevent="changeResultsView('compact')"
                    >
                      <font-awesome-icon
                        :icon="$v6.solid.faGrid3"
                        size="lg"
                      />
                      <span class="sr-only">Show icons in compact display</span>
                    </button>
                    <button
                      class="flat with-border"
                      :style="{ '--button-color': resultsView === 'cheatsheet' ? 'var(--link-color)' : 'var(--fa-navy)' }"
                      aria-label="Cheatsheet"
                      data-balloon-pos="up"
                      @click.prevent="changeResultsView('cheatsheet')"
                    >
                      <font-awesome-icon
                        :icon="$v6.solid.faListDots"
                        size="lg"
                      />
                      <span class="sr-only">Show icons in cheatsheet display</span>
                    </button>
                  </div>

                  <version-selector />
                </div>
              </div>
            </div>
          </div>
        </section>

        <div class="margin-top-2xl margin-bottom-6xl">
          <div class="container">
            <wa-old-version-alert
              v-if="isOlderVersion"
              subject="icons"
            />

            <div class="row roomy">
              <div :class="['column-12 laptop:column-3 desktop:column-2 wrap-icons-facets-menu', { 'is-container-hidden': !isShowingFacetsMenu }]">
                <wa-carbon-ad has-margin />

                <icon-pack-facets
                  v-if="version !== 5"
                  :version="version"
                  @scrollTop="scrollTop"
                  @closeIcon="closeIcon"
                  @applyFacet="applyIconPackFacet"
                />

                <style-list-facets
                  @scrollTop="scrollTop"
                  @closeIcon="closeIcon"
                  @applyFacet="applyStyleFacet"
                />

                <visual-tag-facets
                  v-if="version !== 5"
                  :version="version"
                  @scrollTop="scrollTop"
                  @closeIcon="closeIcon"
                />

                <editorial-facets
                  @scrollTop="scrollTop"
                  @closeIcon="closeIcon"
                />

                <category-facets
                  :selected-category="selectedCategory"
                  @scrollTop="scrollTop"
                  @closeIcon="closeIcon"
                />

                <form
                  id="icons-facets-menu"
                  class="form icons-facets-menu display-flex flex-column"
                >
                  <button
                    type="submit"
                    class="sr-only"
                  >
                    Update Icon Results
                  </button>
                </form>
              </div>

              <div class="column-12 laptop:column-9 desktop:column-10">
                <ais-stats>
                  <div
                    slot-scope="{ nbPages, nbHits, page }"
                    class="icons-results-summary display-flex flex-row flex-wrap flex-items-center laptop:flex-nowrap margin-bottom-xl"
                  >
                    <div class="icons-results-summary-count margin-right-lg">
                      <h1 class="h4 text-nowrap padding-y-5xs">{{ nbHits.toLocaleString() }} Icons</h1>
                    </div>
                    <div class="icons-results-summary-facets">
                      <current-facets
                        :has-search-term="searchTerm ? true : false"
                        @clearSearch="clearSearch"
                        @removeRefinement="removeRefinement"
                      />
                    </div>
                    <div
                      v-if="nbPages > 1 && !groupByStyle"
                      class="icons-results-summary-pages margin-left-lg text-right"
                      style="flex: 1 0 auto"
                    >
                      <p class="margin-bottom-0 muted text-nowrap">
                        <span class="sr-only">Currently on&nbsp;</span>
                        <span class="text-capitalize">page</span>
                        {{ page + 1 }} of {{ nbPages }}
                      </p>
                    </div>
                  </div>
                </ais-stats>

                <no-search-results
                  :version="version"
                  @clearSearch="clearSearch"
                />

                <icon-list
                  ref="iconList"
                  :results-view="resultsView"
                  :selected-family="selectedFamily"
                  :selected-style="selectedStyle"
                  :is-pro-user="isProUser"
                  :selected-icon-id="selectedIconId"
                  :version="version"
                  :search-field-focus="searchFieldFocus"
                  :group-by-family-style="groupByStyle"
                  :has-search-term="searchTerm ? true : false"
                  @toggleIcon="toggleIcon"
                  @closeIcon="closeIcon"
                  @viewMore="viewMore"
                />

                <modal-icon-detail
                  ref="iconModal"
                  :icon-abstract="iconAbstract"
                  :index="index"
                  :selected-from-search="selectedIcon"
                  :icon-name="selectedIconId"
                  :styling="styling"
                  :is-pro-user="isProUser"
                  :is-showing="isShowingIconModal"
                  :is-duotone="isDuotoneIcon"
                  :default-styles="defaultStyles"
                  :viggo="viggo"
                  @closeIcon="closeIcon"
                  @selectCategory="selectCategory"
                  @nextIcon="$refs.iconList.next()"
                  @prevIcon="$refs.iconList.previous()"
                  @stylingChange="stylingChange"
                  @resetStyles="resetStyles"
                />

                <icons-pagination
                  v-if="!groupByStyle"
                  @closeIcon="closeIcon"
                />
              </div>
            </div>
          </div>
        </div>
      </ais-instant-search>
    </main>

    <wa-page-footer slot="footer" />
  </wa-page>
</template>

<script>
  import { isEmpty, sampleSize } from 'lodash'
  import algoliasearch from 'algoliasearch/lite'
  import CategoryFacets from './components/CategoryFacets'
  import CurrentFacets from './components/CurrentFacets'
  import EditorialFacets from './components/EditorialFacets'
  import IconList from './components/IconList'
  import IconsPagination from './components/IconsPagination'
  import InputTerm from './components/InputTerm'
  import ModalIconDetail from 'app/views/icons/components/icons/details/ModalIconDetail'
  import NoSearchResults from './components/NoSearchResults'
  import SearchTabs from './components/SearchTabs'
  import StyleListFacets from './components/StyleListFacets'
  import IconPackFacets from './components/IconPackFacets'
  import VisualTagFacets from './components/VisualTagFacets'
  import VersionSelector from './components/VersionSelector'

  import { BRANDS_HINTS, EASTER_EGG_HINTS, REAL_HINTS } from './hints'
  import { aa, createInsightsMiddleware } from './insightUtils'
  import { DEFAULT_STYLING } from 'app/views/icons/defaults'
  import { EventTrackingMixin, hasAccessToProForActiveVersion, SubscriptionMixin } from 'app/mixins'
  import { fetchIndexName } from './utils'
  import { fetchSvg } from 'app/views/icons/components/icons/details/utils'
  import { glueWithVue } from './routing'
  import { IconsMixins } from 'app/views/icons/components/icons/mixins.js'
  import { loadViggo } from 'app/viggo'
  import { stateToRoute, routeToState } from './algoliaRouteState'
  import { stylingToQuery } from 'app/views/icons/routing.js'

  import search_tabs from './search-tabs.json'
  import search_tabs_v5 from './search-tabs-v5.json'

  const INITIAL_VIEW = 'compact'
  const PAGESIZE = 180

  export default {
    name: 'IconSearch',

    components: {
      InputTerm,
      CategoryFacets,
      CurrentFacets,
      EditorialFacets,
      ModalIconDetail,
      IconList,
      IconPackFacets,
      IconsPagination,
      NoSearchResults,
      SearchTabs,
      StyleListFacets,
      VersionSelector,
      VisualTagFacets
    },

    mixins: [EventTrackingMixin, hasAccessToProForActiveVersion, IconsMixins, SubscriptionMixin],

    data() {
      return {
        hints: sampleSize(REAL_HINTS, 3).concat(sampleSize(BRANDS_HINTS, 2)).concat(sampleSize(EASTER_EGG_HINTS, 1)),
        iconAbstract: null,
        insightsClient: aa,
        isLoading: false,
        middlewares: [createInsightsMiddleware()],
        originalTitle: document.title,
        pageSize: PAGESIZE,
        resultsView: 'compact',
        routing: {
          router: glueWithVue(this),
          stateMapping: {
            stateToRoute: stateToRoute,
            routeToState: routeToState
          }
        },
        searchClient: algoliasearch(FONTAWESOME.ALGOLIA_APPLICATION_ID, FONTAWESOME.ALGOLIA_SEARCH_API_KEY),
        searchFieldFocus: false,
        searchTerm: this.$route.query.q,
        selectedCategory: null,
        selectedIcon: null,
        selectedIconId: null,
        selectedFamily: null,
        selectedStyle: null,
        isShowingFacetsMenu: false,
        isShowingIconModal: false,

        styling: {
          animation: DEFAULT_STYLING.animation,
          colorPrimary: DEFAULT_STYLING.colorPrimary,
          colorSecondary: DEFAULT_STYLING.colorSecondary,
          family: null,
          opacityPrimary: DEFAULT_STYLING.opacityPrimary,
          opacitySecondary: DEFAULT_STYLING.opacitySecondary,
          rotation: DEFAULT_STYLING.rotation,
          size: DEFAULT_STYLING.size,
          style: null
        },
        viggo: null,
        filters: '',
        groupByStyle: true,
        iconPackFacetCount: 0,
        styleFacetCount: 0
      }
    },

    computed: {
      toggleGroupingLabel() {
        return this.groupByStyle ? 'Ungroup by style' : 'Group by style'
      },

      defaultStyles() {
        const stylingDefaults = { ...DEFAULT_STYLING }

        stylingDefaults.family = this.styling.family
        stylingDefaults.style = this.styling.style

        return stylingDefaults
      },

      facetFilters() {
        return [['type:icon']]
      },

      index() {
        return fetchIndexName(this.$releases.active.version)
      },

      isCustomIcon() {
        return this.styling.style === 'custom'
      },

      isOlderVersion() {
        return this.$releases.active.versionParts.major != 6 ? true : false
      },

      facetsMenuLabel() {
        return this.isShowingFacetsMenu ? 'Close Filters' : 'Show Filters'
      },

      isProUser() {
        return this.hasAccessToProForActiveVersion
      },

      searchFocus() {
        return !isEmpty(this.$route.query)
      },

      version() {
        return this.$releases.active.versionParts.major
      },

      selectedTab() {
        return this.$route.query['ic'] ? this.$route.query['ic'] : 'all-icons'
      }
    },

    watch: {
      styling(newValue, oldValue) {
        if (!this.selectedIconId) {
          /* No icon selected, we should go back to search */
          this.routeBackToSearch()
        }

        if (this.selectedIconId && (newValue.family !== oldValue.family || newValue.style !== oldValue.style)) {
          /* Icon selected and the family and style have changed */
          this.routeToIconDetail()
        }

        if (this.selectedIconId && newValue.family === oldValue.family && newValue.style === oldValue.style) {
          /* A styling option changed */
          this.routeWithDifferentStyling()
        }

        /*
         * watching and checking to see if the family or style has changed
         * if changed, we need to fetch the new svg
         * fetchSvg(), in this instance, is ultimately used to get the iconAbstract
         * and pass along to the codeSnippetSvg.vue component
         */
        if (this.selectedIcon && (newValue.family !== oldValue.family || newValue.style !== oldValue.style)) {
          fetchSvg(this.viggo, this.isCustomIcon, this.selectedIcon, this.$releases.active.version, this.styling)
            .then((iconAbstract) => {
              this.iconAbstract = iconAbstract
            })
            .catch((error) => {
              throw new Error('fetchSvg() in watcher', error)
            })
        }
      },

      icon(newValue) {
        if (newValue !== null) {
          document.title = `${newValue.name} Icon | Font Awesome`
        } else {
          document.title = this.originalTitle
        }
      }
    },

    beforeMount() {
      this.isLoading = true
      setTimeout(() => {
        this.isLoading = false
      }, 300)
    },

    async created() {
      if (this.version === 5) {
        // disable grouping for v5
        this.groupByStyle = false
      }

      const storedValue = await this.$getItem('resultsView')
      this.resultsView = storedValue ? storedValue : INITIAL_VIEW
      window.addEventListener('popstate', this.popstate)
      this.loadFilter()
      this.isGroupedCheck()
    },

    mounted() {
      this.$posthog.capture('icon_search_view', this.determinationsEventProps)
    },

    destroyed() {
      window.removeEventListener('popstate', this.popstate)
    },

    methods: {
      isGroupedCheck() {
        const styles = this.getQueryStringArray('s')
        const iconPacks = this.getQueryStringArray('ip')

        if (styles.length === 1 && iconPacks.length === 1) {
          // disable grouping if there is only one style and one icon pack
          this.disableStyleGrouping()
        }
      },

      loadFilter() {
        // Checks for a tab name on the querystring and sets the filters accordingly
        const tabName = this.$route.query['ic']
        const tab = (this.version === 5 ? search_tabs_v5 : search_tabs).find((tab) => tab.name === tabName)
        if (tab && tab.filters) {
          this.applyFilter(tab.filters)
        }
      },

      applyFilter(filter) {
        this.filters = filter

        // TODO: determine if this is needed
        if (this.searchTerm) {
          this.setSearchTerm(this.searchTerm)
        }
      },

      applyIconPackFacet(isRefined) {
        this.iconPackFacetCount = this.iconPackFacetCount + (isRefined ? 1 : -1)
        this.checkFacetCount()
      },

      applyStyleFacet(isRefined) {
        this.styleFacetCount = this.styleFacetCount + (isRefined ? 1 : -1)
        this.checkFacetCount()
      },

      checkFacetCount() {
        if (this.styleFacetCount === 1 && this.iconPackFacetCount === 1) {
          this.disableStyleGrouping()
        } else {
          this.enableStyleGrouping()
        }
      },

      routeWithHistory({ url, using } = { using: history.pushState }) {
        using.call(history, {}, null, url)
      },

      routeBackToSearch() {
        /*
         * We are cheating here and using this.$route because it will be set to whatever
         * the last search parameters were. Since we use the History object directly for
         * this Instragram-style navigation we end up bypassing Vue Router. So conveniently
         * this.$route is equal to the last time Vue Router was in control which happens to
         * be when the search page was loaded or last interacted with.
         */
        this.routeWithHistory({
          using: history.replaceState,
          url: this.$router.resolve({
            name: this.$route.name,
            params: this.$route.params,
            query: this.$route.query
          }).href
        })
      },

      routeToIconDetail({ using } = { using: history.pushState }) {
        this.routeWithHistory({
          using,
          url: this.$router.resolve({
            name: this.$versionRouteName('icons.detail'),
            params: { icon: this.selectedIconId },
            query: stylingToQuery(this.styling)
          }).href
        })
      },

      routeWithDifferentStyling() {
        this.routeToIconDetail({ using: history.replaceState })
      },

      resetStyles() {
        this.styling = { ...this.styling, ...DEFAULT_STYLING }
      },

      stylingChange(styling) {
        this.styling = { ...this.styling, ...styling }
      },

      async toggleIcon(icon) {
        if (!this.viggo) {
          try {
            this.viggo = await loadViggo()
          } catch (error) {
            throw new Error('Error loading viggo', error)
          }
        }

        if (this.selectedIconId == icon.name && this.selectedStyle == icon.style && this.selectedFamily == icon.family) {
          this.closeIcon()
        } else {
          this.selectedIcon = icon
          this.selectedIconId = icon.name
          this.selectedStyle = icon.style
          this.selectedFamily = icon.family
          this.styling = { ...this.styling, family: icon.family, style: icon.style === '' && icon.family === 'brands' ? 'solid' : icon.style }

          this.$refs.iconModal.closeEditor()
          this.isShowingIconModal = true
        }

        if (this.selectedIcon.id) {
          try {
            this.iconAbstract = await fetchSvg(this.viggo, this.isCustomIcon, icon, this.$releases.active.version)
          } catch (error) {
            throw new Error('fetchingSvg()', error)
          }
        }
      },

      popstate() {
        // This function is wired up to popstate (see created). It is triggered when navigating back
        this.closeIcon()
        if (localStorage.applyGroupByStyle === 'enabled') {
          this.enableStyleGrouping()
        }
      },

      closeIcon() {
        if (this.selectedIconId) {
          this.selectedIcon = null
          this.selectedIconId = null
          this.isShowingIconModal = false
          this.styling = { ...this.styling, ...DEFAULT_STYLING, family: null, style: null }
        }
      },

      viewMore({ family, style }) {
        this.disableStyleGrouping()

        // set flag to apply groupByStyle when navigating back
        localStorage.applyGroupByStyle = 'enabled'

        this.iconPackFacetCount = 1
        this.styleFacetCount = 1

        const searchParams = style === 'brands' ? { iconPacks: 'brands' } : { iconPacks: family, styles: style }

        if (this.searchTerm) {
          searchParams['term'] = this.searchTerm
        }

        if (this.$route.query['sponsored']) {
          searchParams['sponsored'] = this.$route.query['sponsored']
        }

        if (this.$route.query['new']) {
          searchParams['new'] = this.$route.query['new']
        }

        if (this.$route.query['favorites']) {
          searchParams['favorites'] = this.$route.query['favorites']
        }

        if (this.$route.query['it']) {
          searchParams['iconTypes'] = this.$route.query['it']
        }

        if (this.$route.query['c']) {
          searchParams['categories'] = this.$route.query['c']
        }

        const href = this.$router.resolve(this.$search(searchParams)).href

        if (href !== this.$route.fullPath) {
          this.$router.push(href)
        }
      },

      suggestionSearch(term) {
        this.setSearchTerm(term)
        this.closeIcon()
      },

      clearSearch(source) {
        if (source !== 'search-bar') {
          this.iconPackFacetCount = 0
          this.styleFacetCount = 0
          this.checkFacetCount()
        }

        this.setSearchTerm('')
        this.closeIcon() // Hide icon when clearing search bar
        this.$posthog.capture('icon_search:clear_search_button_click')
      },

      async setSearchTerm(term) {
        this.searchTerm = term
        this.isShowingDiscovery = false
        this.$refs.instantSearch.instantSearchInstance.helper.setQuery(term).search()
        this.closeIcon()
      },

      scrollTop() {
        const input = document.querySelector('#header')
        input.scrollIntoView()
      },

      async changeResultsView(view) {
        this.resultsView = view
        await this.$setItem('resultsView', view)
        this.$posthog.capture('icon_search:layout_button_click', { layout: view })
      },

      toggleFacetsMenu() {
        this.isShowingFacetsMenu = !this.isShowingFacetsMenu
      },

      toggleFamilyStyleGrouping() {
        if (this.groupByStyle) {
          this.disableStyleGrouping()
        } else {
          this.enableStyleGrouping()
        }
      },

      enableStyleGrouping() {
        if (this.version !== 5) {
          this.groupByStyle = true
        }
      },

      disableStyleGrouping() {
        this.groupByStyle = false
        localStorage.applyGroupByStyle = 'disabled'
      },

      selectCategory(category) {
        this.setSearchTerm('')
        this.selectedCategory = category
      },

      removeRefinement(attribute) {
        // grouping is disabled when refining by style and icon_pack
        // if we removed style or icon_pack refinements then we need to enable grouping
        if (this.iconPackFacetCount > 0 && attribute === 'icon_packs') {
          this.iconPackFacetCount = this.iconPackFacetCount - 1
        }

        if (this.styleFacetCount > 0 && attribute === 'style') {
          this.styleFacetCount = this.styleFacetCount - 1
        }

        this.checkFacetCount()
      },

      getQueryStringArray(queryString) {
        const query = this.$route.query[queryString]
        return query ? query.split(',') : []
      }
    }
  }
</script>
